import axios from "axios";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const BASE = `${SERVER}/api/integrations/chatbot`;

/**
 * GET /api/integrations/chatbot — the workspace's chat widget, or null if
 * none is connected yet (a workspace has at most one — see ChatbotWidgetModel).
 * `includeDeleted` also returns a disconnected (soft-deleted) widget, with its
 * `deletedAt` set, instead of the default null — used to prefill the connect
 * wizard with the last config when reconnecting.
 */
export async function fetchChatbotWidget(workspaceId, token, { includeDeleted = false } = {}) {
  const response = await axios.get(BASE, {
    params: { workspaceId, ...(includeDeleted ? { includeDeleted: true } : {}) },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data?.widget || null;
}

/**
 * POST /api/integrations/chatbot — creates the workspace's widget.
 * `widgetSecret` in the response is returned exactly once — the backend
 * never re-exposes it after this call (or after rotateChatbotWidgetKey).
 */
export async function createChatbotWidget(workspaceId, payload, token) {
  const response = await axios.post(
    BASE,
    { workspaceId, ...payload },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data; // { success, widget, widgetSecret }
}

/** PATCH /api/integrations/chatbot/:id */
export async function updateChatbotWidget(id, workspaceId, payload, token) {
  const response = await axios.patch(
    `${BASE}/${id}`,
    { workspaceId, ...payload },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data?.widget;
}

/** DELETE /api/integrations/chatbot/:id — disconnects (soft-deletes) the widget. */
export async function deleteChatbotWidget(id, workspaceId, token) {
  const response = await axios.delete(`${BASE}/${id}`, {
    params: { workspaceId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

/**
 * POST /api/integrations/chatbot/:id/rotate-key — invalidates the old
 * publicKey/widgetSecret immediately. Same one-time-secret contract as create.
 */
export async function rotateChatbotWidgetKey(id, workspaceId, token) {
  const response = await axios.post(
    `${BASE}/${id}/rotate-key`,
    { workspaceId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data; // { success, widget, widgetSecret }
}
